import {
  CaseStudyContentWrapper,
  CaseStudyImage,
  CaseStudyText,
  HeroSection,
  HeroSectionProps,
} from '@rimactechnology/rimac-ui-lib';
import { InferGetStaticPropsType, NextPage } from 'next';
import { Fragment } from 'react';

import {
  CaseStudiesDocument,
  CaseStudiesQuery,
  CaseStudiesQueryVariables,
  FooterDocument,
  FooterQuery,
  FooterQueryVariables,
  NavigationDocument,
  NavigationQuery,
  NavigationQueryVariables,
} from '@/api';
import { addApolloState, initializeApollo } from '@/api/apolloClient';
import { Layout } from '@/components/layout/Layout';
import { Partners } from '@/components/partners/Partners';
import getCdlPublicId from '@/hooks/useCdlPublicId';

type Params = {
  params: {
    path: string;
  };
};

export const getStaticPaths = async () => {
  const client = initializeApollo();

  // prefetch
  await client.query({ query: CaseStudiesDocument, variables: { limit: -1 } });

  const { data } = await client.query({
    query: CaseStudiesDocument,
    variables: { limit: -1 },
  });

  const caseStudies: CaseStudiesQuery = data;

  const urls = caseStudies?.caseStudies?.data?.map(
    (item) => item.attributes?.slug,
  );

  const paths = urls?.map((item) => ({
    params: {
      path: item,
    },
  }));

  return { paths, fallback: false };
};

export async function getStaticProps({ params }: Params) {
  const client = initializeApollo();

  // prefetch
  await client.query({ query: NavigationDocument });
  await client.query({ query: FooterDocument });
  await client.query({
    query: CaseStudiesDocument,
    variables: { limit: -1, slug: params.path },
  });

  const { data: caseStudiesData } = await client.query<
    CaseStudiesQuery,
    CaseStudiesQueryVariables
  >({
    query: CaseStudiesDocument,
    variables: { limit: -1, slug: params.path },
  });

  const { data: headerData } = await client.query<
    NavigationQuery,
    NavigationQueryVariables
  >({
    query: NavigationDocument,
  });

  const { data: footerData } = await client.query<
    FooterQuery,
    FooterQueryVariables
  >({
    query: FooterDocument,
  });

  return addApolloState(client, {
    props: { headerData, footerData, caseStudiesData },
  });
}

const CaseStudyDetail: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ headerData, footerData, caseStudiesData }) => {
  const caseStudyDetail = caseStudiesData?.caseStudies?.data[0]?.attributes;

  const heroSectionProps: HeroSectionProps = {
    useCase: 'technology',
    enabledLazyLoading: true,
    titleMaxWidth: undefined,
    subtitleMaxWidth: undefined,
    title: caseStudyDetail?.hero?.title || '',
    subtitle: caseStudyDetail?.hero?.subtitle || '',
    button: caseStudyDetail?.hero?.button
      ? {
          useCase: 'technology',
          label: caseStudyDetail?.hero?.button?.label || '',
          path: caseStudyDetail?.hero?.button?.path || '',
          openInNewTab: !!caseStudyDetail?.hero?.button?.openInNewTab,
        }
      : undefined,
    heroImage: {
      image: {
        imgSrc: getCdlPublicId(
          caseStudyDetail?.hero?.heroImage?.img_desktop?.data?.attributes
            ?.url || '',
        ),
        imgSrcMobile: getCdlPublicId(
          caseStudyDetail?.hero?.heroImage?.img_mobile?.data?.attributes?.url ||
            '',
        ),
        alt: caseStudyDetail?.hero?.heroImage?.alt || '',
        blurHash:
          caseStudyDetail?.hero?.heroImage?.img_desktop?.data?.attributes
            ?.blurhash || '12312312',
      },
    },
  };

  return (
    <Layout footerData={footerData} headerData={headerData}>
      <HeroSection {...heroSectionProps} />
      <CaseStudyContentWrapper>
        <Fragment>
          {caseStudyDetail?.content?.map((content, index) => {
            switch (content?.__typename) {
              case 'ComponentCaseStudyTextContent':
                return (
                  <CaseStudyText richText={content?.text} parentIndex={index} />
                );
              case 'ComponentCaseStudyGallery':
                return (
                  <CaseStudyImage
                    image={{
                      imgSrc: getCdlPublicId(
                        content?.images?.[0]?.img_desktop?.data?.attributes
                          ?.url,
                      ),
                      imgSrcMobile: getCdlPublicId(
                        content?.images?.[0]?.img_mobile?.data?.attributes?.url,
                      ),
                      alt: content?.images?.[0]?.alt || '',
                      blurHash:
                        content?.images?.[0]?.img_desktop?.data?.attributes
                          ?.blurhash || '12312312',
                    }}
                  />
                );
            }
          })}
        </Fragment>
      </CaseStudyContentWrapper>
      <Partners data={caseStudyDetail?.partnersBlogSection} />
    </Layout>
  );
};

export default CaseStudyDetail;
